import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { CheckCircle, XCircle, Copy, Check, Lock, Sparkles, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { copyToClipboard } from '../utils/clipboard';

interface CommunicationTemplatesProps {
  rtiScore: number;
  tier: 'free' | 'premium' | 'pro';
}

interface Template {
  id: string;
  type: 'dont' | 'do';
  category: string;
  message: string;
  explanation?: string;
}

export function CommunicationTemplates({ rtiScore, tier }: CommunicationTemplatesProps) {
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const isPremium = tier !== 'free';
  const isPro = tier === 'pro';

  const getRTIState = () => {
    if (rtiScore >= 61) return 'high-tension';
    if (rtiScore >= 36) return 'elastic';
    return 'stable';
  };

  const state = getRTIState();

  const getTemplates = (): Template[] => {
    if (state === 'high-tension') {
      return [
        {
          id: 'ht-dont-1',
          type: 'dont',
          category: 'Anxious pursuit',
          message: "Why haven't you responded? Are we okay?",
          explanation: 'This communicates anxiety and need for reassurance, which creates pressure',
        },
        {
          id: 'ht-dont-2',
          type: 'dont',
          category: 'Over-checking',
          message: "Did you see my message?",
          explanation: 'Shows you\'re monitoring their behavior and feeling insecure',
        },
        {
          id: 'ht-dont-3',
          type: 'dont',
          category: 'Emotional dumping',
          message: "I feel like you're pulling away and it's making me anxious...",
          explanation: 'Places emotional burden on them when they\'re already distancing',
        },
        {
          id: 'ht-do-1',
          type: 'do',
          category: 'Strategic restraint',
          message: '[Practice restraint - don\'t send anything]',
          explanation: 'Space is your most powerful tool. Let them come to you',
        },
        {
          id: 'ht-do-2',
          type: 'do',
          category: 'If they reach out',
          message: "Hey! Good to hear from you 😊",
          explanation: 'Warm but brief. Shows you\'re not angry but also not desperately waiting',
        },
        {
          id: 'ht-do-3',
          type: 'do',
          category: 'If you must initiate',
          message: "[Light content: meme, article, or \"this made me think of you\" with no pressure to respond]",
          explanation: 'Low-investment contact that doesn\'t require response',
        },
      ];
    }

    if (state === 'elastic') {
      return [
        {
          id: 'el-dont-1',
          type: 'dont',
          category: 'Relationship talk',
          message: "We need to talk about us",
          explanation: 'Creates anxiety when things are already delicate',
        },
        {
          id: 'el-dont-2',
          type: 'dont',
          category: 'Needy questions',
          message: "Do you still like me?",
          explanation: 'Seeks validation which reduces attraction',
        },
        {
          id: 'el-do-1',
          type: 'do',
          category: 'Light reconnection',
          message: "Saw this and thought of you [photo/meme]",
          explanation: 'Creates positive association without pressure',
        },
        {
          id: 'el-do-2',
          type: 'do',
          category: 'Quality time offer',
          message: "Want to grab dinner this week? Been thinking about that place you mentioned",
          explanation: 'Specific, casual invitation that shows you remember details',
        },
        {
          id: 'el-do-3',
          type: 'do',
          category: 'Positive reflection',
          message: "Had fun with you last time. Let's do something similar soon",
          explanation: 'References positive past experience and creates future anticipation',
        },
        {
          id: 'el-do-4',
          type: 'do',
          category: 'Shared interest',
          message: "Just saw [thing related to their interest]. Made me think you'd appreciate this",
          explanation: 'Shows you pay attention to what they care about',
        },
      ];
    }

    // Stable state
    return [
      {
        id: 'st-do-1',
        type: 'do',
        category: 'Maintain momentum',
        message: "Continue your natural communication rhythm - things are healthy",
        explanation: 'Don\'t overthink when things are good',
      },
      {
        id: 'st-do-2',
        type: 'do',
        category: 'Deepen connection',
        message: "I really appreciated when you [specific thing they did]",
        explanation: 'Specific appreciation strengthens bond',
      },
      {
        id: 'st-do-3',
        type: 'do',
        category: 'Vulnerability',
        message: "I've been thinking about [meaningful topic] and wanted to hear your perspective",
        explanation: 'Safe to be vulnerable when foundation is strong',
      },
      {
        id: 'st-do-4',
        type: 'do',
        category: 'Planning together',
        message: "I'd love to plan something special for us. What sounds good to you?",
        explanation: 'Creates shared future and shows investment',
      },
      {
        id: 'st-do-5',
        type: 'do',
        category: 'Affirmation',
        message: "Just wanted to say I'm really glad we're doing this",
        explanation: 'Affirms the relationship without neediness',
      },
    ];
  };

  const templates = getTemplates();
  const dontTemplates = templates.filter(t => t.type === 'dont');
  const doTemplates = templates.filter(t => t.type === 'do');

  const handleCopy = (text: string, id: string) => {
    // Remove bracket instructions for copying
    const cleanText = text.replace(/\[.*?\]/g, '').trim();
    
    if (cleanText) {
      copyToClipboard(cleanText);
      setCopiedId(id);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  if (!isPremium) {
    return (
      <Card className="p-6 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-green-100 rounded-lg">
            <Lock className="w-5 h-5 text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-neutral-900 mb-1">Communication Templates</h3>
            <p className="text-sm text-neutral-600 mb-3">
              Get state-specific message templates based on your RTI score. Know what to say (and not say) in each situation.
            </p>
            <Button
              size="sm"
              onClick={() => window.location.href = '/settings'}
              className="gap-2"
            >
              Unlock with Premium
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gradient-to-br from-green-50 to-teal-50 border-green-200">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-green-600" />
            <h3 className="font-medium text-neutral-900">Communication Templates</h3>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-medium ${
            state === 'high-tension' 
              ? 'bg-red-100 text-red-700'
              : state === 'elastic'
              ? 'bg-yellow-100 text-yellow-700'
              : 'bg-green-100 text-green-700'
          }`}>
            {state === 'high-tension' && 'High Tension'}
            {state === 'elastic' && 'Elastic'}
            {state === 'stable' && 'Stable'}
          </div>
        </div>

        <p className="text-sm text-neutral-600">
          {state === 'high-tension' && 'Your RTI is high. Focus on restraint and reducing pressure.'}
          {state === 'elastic' && 'Your RTI is moderate. Light reconnection is appropriate.'}
          {state === 'stable' && 'Your RTI is healthy. Deepen connection and maintain momentum.'}
        </p>

        {/* Don't Templates */}
        {dontTemplates.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <XCircle className="w-4 h-4 text-red-600" />
              <h4 className="text-sm font-medium text-neutral-900">Don't Say</h4>
            </div>
            {dontTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-red-50 border border-red-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <p className="text-xs text-red-600 font-medium mb-1">{template.category}</p>
                    <p className="text-sm text-red-900 italic">"{template.message}"</p>
                  </div>
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                </div>
                {template.explanation && (
                  <p className="text-xs text-red-700 mt-2">
                    Why avoid: {template.explanation}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Do Templates */}
        {doTemplates.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <h4 className="text-sm font-medium text-neutral-900">Try Instead</h4>
            </div>
            {doTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-green-50 border border-green-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1">
                    <p className="text-xs text-green-600 font-medium mb-1">{template.category}</p>
                    <p className="text-sm text-green-900">
                      {template.message.startsWith('[') ? (
                        <span className="italic">{template.message}</span>
                      ) : (
                        `"${template.message}"`
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    {!template.message.startsWith('[') && (
                      <button
                        onClick={() => handleCopy(template.message, template.id)}
                        className="p-1 hover:bg-green-100 rounded transition-colors"
                      >
                        {copiedId === template.id ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4 text-green-600" />
                        )}
                      </button>
                    )}
                  </div>
                </div>
                {template.explanation && (
                  <p className="text-xs text-green-700 mt-2">
                    Why this works: {template.explanation}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pro: AI-Generated Templates */}
        {isPro && (
          <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">PRO</span>
                  <span className="text-sm font-medium text-purple-900">AI-Personalized Templates</span>
                </div>
                <p className="text-xs text-purple-700 mb-3">
                  Coming soon: Get AI-generated message templates based on your specific relationship history, 
                  communication patterns, and current RTI state.
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-purple-300 text-purple-700 hover:bg-purple-100"
                  disabled
                >
                  Generate Custom Templates
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="bg-neutral-50 rounded-lg p-3 border border-neutral-200">
          <p className="text-xs text-neutral-600">
            <strong>Remember:</strong> Templates are guidelines. Adapt them to your natural communication style 
            and relationship context. Authenticity matters more than perfect wording.
          </p>
        </div>
      </div>
    </Card>
  );
}